/**
 * LeadCard Component - Mobile-first card component for displaying leads
 * Displays lead information in a tap-friendly card format
 */

import React from "react";
import { Card, Typography, Button, Box, Chip } from "@mui/material";
import { WhatsApp, Description, Schedule, Map } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import StatusBadge from "./StatusBadge.jsx";
import OriginBadge from "./OriginBadge.jsx";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import { format } from "date-fns";
import { logError } from "@/shared/utils/logger.js";

const StyledCard = styled(Card)({
  marginBottom: "16px",
  borderRadius: "12px",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  cursor: "pointer",
  border: "1px solid transparent",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-1px)",
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
});

const CardContent = styled(Box)({
  padding: "16px",
});

const HeaderRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "12px",
});

const NamePhoneRow = styled(Box)({
  flex: 1,
});

const NameText = styled(Typography)({
  fontWeight: 600,
  fontSize: "1rem",
  marginBottom: "4px",
});

const PhoneText = styled(Typography)({
  fontSize: "0.875rem",
});

const RouteRow = styled(Box)({
  marginBottom: "12px",
  padding: "8px 0",
});

const RouteText = styled(Typography)({
  fontSize: "0.875rem",
  marginBottom: "4px",
  "&:last-child": {
    marginBottom: 0,
  },
});

const BadgesRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginBottom: "12px",
  alignItems: "center",
});

const ActionsRow = styled(Box)({
  display: "flex",
  gap: "8px",
  marginTop: "12px",
});

const CountBadge = styled(Chip)({
  fontWeight: 600,
  fontSize: "0.7rem",
  height: "20px",
});

const formatPhone = (phone) => {
  if (!phone) return "N/A";
  return phone.replace("+27", "0");
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch {
    return dateString;
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MMM dd, yyyy HH:mm");
  } catch {
    return dateString;
  }
};

/**
 * LeadCard Component
 * @param {Object} props
 * @param {Object} props.lead - Lead object
 * @param {number} [props.count] - Number of leads for this phone (for grouped leads)
 * @param {Function} props.onWhatsApp - Callback when WhatsApp button clicked
 * @param {Function} props.onQuote - Callback when Quote button clicked
 * @param {Function} props.onFollowUp - Callback when Follow-up button clicked
 * @param {Function} props.onView - Callback when card is clicked
 * @param {Function} props.onAccept - Callback when accepting lead
 * @param {Function} props.onReject - Callback when rejecting lead
 * @param {Function} props.onRoute - Callback when route button clicked
 */
const LeadCard = ({
  lead,
  count,
  onWhatsApp,
  onQuote,
  onFollowUp,
  onView,
  onAccept,
  onReject,
  onRoute,
}) => {
  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between("md", "lg")
  );

  if (!lead) return null;

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    try {
      if (onWhatsApp) {
        onWhatsApp(lead);
      }
    } catch (error) {
      logError("Failed to open WhatsApp share", {
        leadId: lead._id || lead.id,
        error: error.message,
      });
    }
  };

  const handleQuote = (e) => {
    e.stopPropagation();
    if (onQuote) {
      onQuote(lead);
    }
  };

  const handleFollowUp = (e) => {
    e.stopPropagation();
    if (onFollowUp) {
      onFollowUp(lead);
    }
  };

  const handleRoute = (e) => {
    e.stopPropagation();
    if (onRoute) {
      onRoute(lead, e);
    }
  };

  const handleCardClick = () => {
    if (onView) {
      onView(lead);
    }
  };

  const getPrimaryAction = () => {
    // Determine primary action based on status
    if (lead.status === "new" || lead.status === "pending") {
      return "quote"; // Quote is primary for new leads
    }
    if (lead.status === "accepted") {
      return "followup"; // Follow-up is primary for accepted leads
    }
    return "whatsapp"; // Default to WhatsApp
  };

  const primaryAction = getPrimaryAction();
  const showCountBadge = count && count > 1;

  return (
    <StyledCard
      onClick={handleCardClick}
      sx={{
        "&:hover": {
          borderColor: "rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <CardContent>
        <HeaderRow>
          <NamePhoneRow>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <NameText
                variant="body1"
                component="div"
                sx={{ color: "rgba(0, 0, 0, 0.87)" }}
              >
                {lead.name || "N/A"}
              </NameText>
              {showCountBadge && (
                <CountBadge
                  label={`${count} enquiries`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 152, 0, 0.1)",
                    color: "rgba(255, 152, 0, 0.9)",
                  }}
                />
              )}
            </Box>
            <PhoneText variant="body2" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              {formatPhone(lead.phone)}
            </PhoneText>
          </NamePhoneRow>
          <OriginBadge lead={lead} />
        </HeaderRow>

        <RouteRow
          sx={{
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.12)",
          }}
        >
          <RouteText variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
            <strong>From:</strong> {lead.moving_from || lead.from || "N/A"}
          </RouteText>
          <RouteText variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
            <strong>To:</strong> {lead.moving_to || lead.to || "N/A"}
          </RouteText>
          <RouteText
            variant="body2"
            sx={{ mt: 1, color: "rgba(0, 0, 0, 0.87)" }}
          >
            <Schedule
              fontSize="small"
              sx={{ mr: 0.5, verticalAlign: "middle" }}
            />
            Move: {formatDate(lead.move_date || lead.date)}
          </RouteText>
          <RouteText variant="body2" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
            Created: {formatDateTime(lead.createdAt)}
          </RouteText>
          {onRoute &&
            (lead.moving_from || lead.from) &&
            (lead.moving_to || lead.to) && (
              <Button
                variant="outlined"
                startIcon={<Map />}
                onClick={handleRoute}
                size="small"
                sx={{
                  mt: 1,
                  textTransform: "none",
                  width: "100%",
                }}
              >
                View Route Map
              </Button>
            )}
        </RouteRow>

        <BadgesRow>
          <StatusBadge status={lead.status} />
          {lead?.followUpSummary && (
            <FollowUpMention
              summary={lead.followUpSummary}
              onClick={(e) => {
                e.stopPropagation();
                handleFollowUp(e);
              }}
            />
          )}
          {/* Show origin badge inline on tablet for denser layout */}
          {isTablet && <OriginBadge lead={lead} />}
        </BadgesRow>

        <ActionsRow>
          <Button
            variant="contained"
            startIcon={<WhatsApp />}
            onClick={handleWhatsApp}
            fullWidth
            sx={{
              flex: 1,
              minHeight: 44,
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#25d366",
              color: "white",
              "&:hover": {
                backgroundColor: "#20ba5a",
                boxShadow: "0 2px 4px rgba(37, 211, 102, 0.3)",
              },
            }}
          >
            WhatsApp
          </Button>
          {primaryAction === "quote" && onQuote && (
            <Button
              variant="contained"
              startIcon={<Description />}
              onClick={handleQuote}
              fullWidth
              sx={{
                flex: 1,
                minHeight: 44,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Quote
            </Button>
          )}
          {primaryAction === "followup" && onFollowUp && (
            <Button
              variant="outlined"
              startIcon={<Schedule />}
              onClick={handleFollowUp}
              fullWidth
              sx={{
                flex: 1,
                minHeight: 44,
                textTransform: "none",
              }}
            >
              Follow-up
            </Button>
          )}
        </ActionsRow>
      </CardContent>
    </StyledCard>
  );
};

export default LeadCard;
